'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/landing-page' },
      { label: 'How It Works', href: '/landing-page' },
      { label: 'Careers', href: '/landing-page' },
      { label: 'Press', href: '/landing-page' }
    ],
    services: [
      { label: 'Mental Health', href: '/health-vertical-pages?category=mental-health' },
      { label: 'Chronic Conditions', href: '/health-vertical-pages?category=chronic-conditions' },
      { label: "Women\'s Health", href: '/health-vertical-pages?category=womens-health' },
      { label: 'Pediatric Care', href: '/health-vertical-pages?category=pediatric-care' }
    ],
    support: [
      { label: 'Help Center', href: '/landing-page' },
      { label: 'Contact Us', href: '/landing-page' },
      { label: 'Privacy Policy', href: '/landing-page' },
      { label: 'Terms of Service', href: '/landing-page' }
    ]
  };

  const socialLinks = [
    { name: 'Facebook', icon: 'ShareIcon', href: '#' },
    { name: 'Twitter', icon: 'ChatBubbleLeftIcon', href: '#' },
    { name: 'Instagram', icon: 'PhotoIcon', href: '#' },
    { name: 'LinkedIn', icon: 'BriefcaseIcon', href: '#' }
  ];

  if (!isHydrated) {
    return (
      <footer className="bg-card border-t border-border pt-16 pb-8">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div>
              <Link href="/landing-page" className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="HeartIcon" size={24} className="text-primary-foreground" variant="solid" />
                </div>
                <span className="text-xl font-heading font-semibold text-foreground">HealConnect</span>
              </Link>
              <p className="text-muted-foreground mb-4">
                Transforming healthcare through accessible, secure, and personalized telehealth services.
              </p>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-foreground mb-4">Company</h3>
              <ul className="space-y-2">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    <Link href={link.href} className="text-muted-foreground transition-smooth hover:text-primary">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-foreground mb-4">Services</h3>
              <ul className="space-y-2">
                {footerLinks.services.map((link) => (
                  <li key={link.label}>
                    <Link href={link.href} className="text-muted-foreground transition-smooth hover:text-primary">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-foreground mb-4">Support</h3>
              <ul className="space-y-2">
                {footerLinks.support.map((link) => (
                  <li key={link.label}>
                    <Link href={link.href} className="text-muted-foreground transition-smooth hover:text-primary">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-border pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                © 2026 HealConnect. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-card border-t border-border pt-16 pb-8">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div>
            <Link href="/landing-page" className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="HeartIcon" size={24} className="text-primary-foreground" variant="solid" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">HealConnect</span>
            </Link>
            <p className="text-muted-foreground mb-4">
              Transforming healthcare through accessible, secure, and personalized telehealth services.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-muted flex items-center justify-center transition-smooth hover:bg-primary hover:text-primary-foreground"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-foreground mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link href={link.href} className="text-muted-foreground transition-smooth hover:text-primary">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-foreground mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link href={link.href} className="text-muted-foreground transition-smooth hover:text-primary">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-foreground mb-4">Support</h3>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link href={link.href} className="text-muted-foreground transition-smooth hover:text-primary">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {currentYear} HealConnect. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <Link href="/landing-page" className="transition-smooth hover:text-primary">Privacy Policy</Link>
              <Link href="/landing-page" className="transition-smooth hover:text-primary">Terms of Service</Link>
              <Link href="/landing-page" className="transition-smooth hover:text-primary">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;